import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { SearchResult, useGlobalSearch } from '../../hooks/useGlobalSearch';
import SearchHelpModal from './SearchHelpModal';
import Icon from '../../../components/icon/Icon';
import './GlobalSearch.css';
import Popovers from '../../../components/bootstrap/Popovers';

const TRANSACTION_TYPES = ['remit', 'power', 'topup', 'withdrawal', 'refund', 'paybill', 'banking', 'event', 'wallet_transaction', 'agent_transaction', 'product', 'kalpeh', 'ticketTransfer', 'airtime', 'cashback'];

interface GlobalSearchProps {
	resetTrigger?: boolean;
}

const GlobalSearch: React.FC<GlobalSearchProps> = ({ resetTrigger }) => {
	const [searchTerm, setSearchTerm] = useState('');
	const [results, setResults] = useState<SearchResult[]>([]);
	const [isOpen, setIsOpen] = useState(false);
	const [selectedIndex, setSelectedIndex] = useState(-1);
	const [showHelpModal, setShowHelpModal] = useState(false);
	const searchRef = useRef<HTMLInputElement>(null);
	const dropdownRef = useRef<HTMLDivElement>(null);
	const navigate = useNavigate();
	const { performSearch, isLoading } = useGlobalSearch();

	const getTypeColor = (type: string): string => {
		const colors = [
			'#FF6B6B', // Red
			'#4ECDC4', // Teal  
			'#45B7D1', // Blue
			'#96CEB4', // Green
			'#FFEAA7', // Yellow
			'#DDA0DD', // Plum
			'#98D8C8', // Mint
			'#F7DC6F', // Light Yellow
			'#BB8FCE', // Light Purple
			'#85C1E9', // Light Blue
			'#F8C471', // Orange
			'#82E0AA', // Light Green
			'#F1948A', // Light Red
			'#85D5F7', // Sky Blue
			'#F4D03F', // Gold
		];
		
		if (!type) return colors[0];
		
		let hash = 0;
		for (let i = 0; i < type.length; i++) {
			hash = type.charCodeAt(i) + ((hash << 5) - hash);
		}
		return colors[Math.abs(hash) % colors.length];
	};

	const handleSearch = async (query: string) => {
		if (!query.trim()) {
			setResults([]);
			return;
		}

		const searchResults = await performSearch(query);
		setResults(searchResults);
	};

	useEffect(() => {
		const debounceTimer = setTimeout(() => {
			handleSearch(searchTerm);
		}, 300);

		return () => clearTimeout(debounceTimer);
	}, [searchTerm]);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
				setIsOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === '/' && event.ctrlKey) {
				event.preventDefault();
				searchRef.current?.focus();
			}
		};

		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, []);

	useEffect(() => {
		if (resetTrigger) {
			setSearchTerm('');
			setResults([]);
			setIsOpen(false);
			setSelectedIndex(-1);
		}
	}, [resetTrigger]);

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setSearchTerm(value);
		setIsOpen(value.length > 0);
		setSelectedIndex(-1);
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (!isOpen) return;

		switch (e.key) {
			case 'ArrowDown':
				e.preventDefault();
				setSelectedIndex((prev) => (prev < results.length - 1 ? prev + 1 : prev));
				break;
			case 'ArrowUp':
				e.preventDefault();
				setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
				break;
			case 'Enter':
				e.preventDefault();
				if (selectedIndex >= 0 && results[selectedIndex]) {
					handleResultClick(results[selectedIndex]);
				}
				break;
			case 'Escape':
				setIsOpen(false);
				setSelectedIndex(-1);
				searchRef.current?.blur();
				break;
		}
	};

	const handleResultClick = (result: SearchResult) => {
		setIsOpen(false);
		setSearchTerm('');
		setSelectedIndex(-1);

		if (TRANSACTION_TYPES.includes(result.type)) {
			navigate('/operations-services/manage-transactions', { 
				state: { 
					openTransactionModal: true, 
					transactionReference: result.title 
				} 
			});
			toast.info(`Found ${result.type} transaction: ${result.title}`);
		} else {
			switch (result.type) {
				case 'customer':
					if (result.id) {
						navigate(`/network-management/customers/${result.id}`);
						toast.info(`Navigating to customer: ${result.title}`);
					} else {
						navigate('/network-management/customers');
						toast.info(`Searching for customer: ${result.title}`);
					}
					break;
				case 'normal':
				case 'master':
					if (result.id) {
						navigate(`/network-management/agents/${result.id}`);
						toast.info(`Navigating to ${result.type} agent: ${result.title}`);
					} else {
						navigate('/network-management/agents');
						toast.info(`Searching for ${result.type} agent: ${result.title}`);
					}
					break;
				case 'merchant':
					if (result.id) {
						navigate(`/network-management/merchants/merchant/${result.id}`);
						toast.info(`Navigating to merchant: ${result.title}`);
					} else {
						navigate('/network-management/merchants/merchant');
						toast.info(`Searching for merchant: ${result.title}`);
					}
					break;
				default:
					toast.info(`Found: ${result.title}`);
					break;
			}
		}
	};

	const getResultIcon = (result: SearchResult) => {
		switch (result.type) {
			// Transaction types
			case 'remit':
				return <Icon icon='Send' className='text-success' />;
			case 'power':
				return <Icon icon='Power' className='text-warning' />;
			case 'topup':
				return <Icon icon='Add' className='text-info' />;
			case 'withdrawal':
				return <Icon icon='Remove' className='text-danger' />;
			case 'refund':
				return <Icon icon='Undo' className='text-secondary' />;
			case 'paybill':
				return <Icon icon='Receipt' className='text-primary' />;
			case 'banking':
				return <Icon icon='AccountBalance' className='text-info' />;
			case 'event':
				return <Icon icon='Event' className='text-warning' />;
			case 'wallet_transaction':
				return <Icon icon='AccountBalanceWallet' className='text-purple-600' />;
			case 'agent_transaction':
				return <Icon icon='Store' className='text-info' />;
			case 'product':
				return <Icon icon='Inventory' className='text-success' />;
			case 'kalpeh':
				return <Icon icon='LocalAtm' className='text-primary' />;
			case 'ticketTransfer':
				return <Icon icon='ConfirmationNumber' className='text-info' />;
			case 'airtime':
				return <Icon icon='Phone' className='text-success' />;
			case 'cashback':
				return <Icon icon='MoneyOff' className='text-success' />;
			
			// User types
			case 'customer':
				return <Icon icon='Person' className='text-primary' />;
			case 'merchant':
				return <Icon icon='Business' className='text-purple-600' />;
			
			// Agent types
			case 'normal':
				return <Icon icon='Store' className='text-info' />;
			case 'master':
				return <Icon icon='SupervisorAccount' className='text-warning' />;
			
			default:
				return <Icon icon='Description' className='text-muted' />;
		}
	};

	return (
		<>
			<div className='modern-search-container' ref={dropdownRef}>
				<div className='search-input-wrapper'>
					<div className='search-input-group'>
						<div className='search-icon-left'>
							{isLoading ? (
								<div className='search-spinner'>
									<div className='spinner-border spinner-border-sm' role='status'>
										<span className='visually-hidden'>Loading...</span>
									</div>
								</div>
							) : (
								<Icon icon='Search' className='search-icon' />
							)}
						</div>
						
						<input
							ref={searchRef}
							type='text'
							className='modern-search-input'
							placeholder='Search customers, merchants, agents, transactions...'
							value={searchTerm}
							onChange={handleInputChange}
							onKeyDown={handleKeyDown}
							onFocus={() => searchTerm && setIsOpen(true)}
						/>
						
						<div className='search-actions'>
							<div className='keyboard-shortcut'>
								<kbd>Ctrl</kbd> + <kbd>/</kbd>
							</div>
							<div className='search-divider'></div>
							<Popovers trigger='hover' desc='Search Tips & Shortcuts'>
								<button
									type='button'
									className='search-help-btn'
									onClick={() => setShowHelpModal(true)}
									aria-label='Search help'>
									<Icon icon='Help' />
								</button>
							</Popovers>
						</div>
					</div>
				</div>

				{isOpen && (
					<div className='search-dropdown'>
						{results.length > 0 ? (
							<>
								<div className='search-results-header'>
									<Icon icon='SearchIcon' className='results-icon' />
									<span className='results-count'>
										{results.length} result{results.length !== 1 ? 's' : ''} found
									</span>
								</div>
								<div className='search-results-list'>
									{results.map((result: any, index: any) => (
										<div
											key={result.id}
											className={`search-result-item ${
												index === selectedIndex ? 'selected' : ''
											}`}
											onClick={() => handleResultClick(result)}
											onMouseEnter={() => setSelectedIndex(index)}>
											<div className='result-icon'>
												{getResultIcon(result)}
											</div>
											<div className='result-content'>
												<div className='result-title'>
													{result.title}
													{(result.type === 'normal' || result.type === 'master') && (
														<span className={`badge badge-${result.type === 'master' ? 'warning' : 'info'} ms-2 small`}>
															{result.type} agent
														</span>
													)}
													{result.type && TRANSACTION_TYPES.includes(result.type) && (
														<span className='badge badge-success ms-2 small'>
															{result.type}
														</span>
													)}
												</div>
												<div className='result-subtitle'>
													{result.subtitle}
													{result.phone && result.phone !== result.subtitle && (
														<span className="text-muted ms-2">• {result.phone}</span>
													)}
												</div>
												{(result.businessName || result.email) && (
													<div className='result-extra text-muted small'>
														{result.businessName && <span>Business: {result.businessName}</span>}
														{result.businessName && result.email && <span> • </span>}
														{result.email && <span>{result.email}</span>}
													</div>
												)}
											</div>
											<div 
												className={`result-status type-${result.type || 'unknown'}`}
												style={{ 
													backgroundColor: getTypeColor(result.type),
													color: '#ffffff',
													fontWeight: '600',
													fontSize: '0.75rem',
													padding: '0.25rem 0.5rem',
													borderRadius: '12px',
													textShadow: '0 1px 2px rgba(0,0,0,0.1)'
												}}>
												{result.type === 'normal' ? 'Normal Agent' : 
												 result.type === 'master' ? 'Master Agent' :
												 result.type === 'customer' ? 'Customer' :
												 result.type === 'merchant' ? 'Merchant' :
												 result.type === 'remit' ? 'Remit' :
												 result.type === 'power' ? 'Power' :
												 result.type === 'topup' ? 'Top Up' :
												 result.type === 'withdrawal' ? 'Withdrawal' :
												 result.type === 'refund' ? 'Refund' :
												 result.type === 'paybill' ? 'Pay Bill' :
												 result.type === 'banking' ? 'Banking' :
												 result.type === 'event' ? 'Event' :
												 result.type === 'wallet_transaction' ? 'Wallet Transaction' :
												 result.type === 'agent_transaction' ? 'Agent Transaction' :
												 result.type === 'product' ? 'Product' :
												 result.type === 'kalpeh' ? 'Kalpeh' :
												 result.type === 'ticketTransfer' ? 'Ticket Transfer' :
												 result.type === 'airtime' ? 'Airtime' :
												 result.type === 'cashback' ? 'Cashback' :
												 result.type ? (result.type.charAt(0).toUpperCase() + result.type.slice(1)) : 'Unknown'}
											</div>
											<div className='result-arrow'>
												<Icon icon='ChevronRight' />
											</div>
										</div>
									))}
								</div>
							</>
						) : searchTerm && !isLoading ? (
							<div className='search-no-results'>
								<div className='no-results-icon'>
									<Icon icon='SearchOff' />
								</div>
								<div className='no-results-title'>No results found</div>
								<div className='no-results-subtitle'>
									Try searching with different keywords or check your spelling
								</div>
							</div>
						) : null}
					</div>
				)}
			</div>

			<SearchHelpModal isOpen={showHelpModal} setIsOpen={setShowHelpModal} />
		</>
	);
};

export default GlobalSearch;

